import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildFeed, priceUsd, type FeedListing } from "../feed-state";
import { type Density } from "../density";
import { useFeed, type ConnectionStatus } from "../hooks/useFeed";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { DensityPicker } from "./DensityPicker";
import { DEFAULT_FILTERS, Filters, type FilterState } from "./Filters";
import { FeedItem } from "./FeedItem";

const FILTERS_KEY = "feed.filters.v1";
const DENSITY_KEY = "feed.density.v1";
const DEFAULT_DENSITY: Density = "cozy";
const BOTTOM_TOLERANCE = 40;
const TOP_LOAD_THRESHOLD = 200;

const STATUS_LABELS: Record<ConnectionStatus, string> = {
  connecting: "connecting",
  connected: "live",
  disconnected: "offline",
};

const STATUS_DOT: Record<ConnectionStatus, string> = {
  connecting: "bg-amber-400",
  connected: "bg-emerald-400",
  disconnected: "bg-rose-400",
};

function passesFilter(l: FeedListing, f: FilterState): boolean {
  if (f.hideClosed && l.status !== "listed") return false;
  if (f.minPriceUsd != null || f.maxPriceUsd != null) {
    const usd = priceUsd(l.payload);
    // non-USDC listings (SOL, ME, ...) pass through — different unit, can't
    // compare without an FX feed
    if (usd != null) {
      if (f.minPriceUsd != null && usd < f.minPriceUsd) return false;
      if (f.maxPriceUsd != null && usd > f.maxPriceUsd) return false;
    }
  }
  return true;
}

export function Feed() {
  const { events, loadingOlder, hasMoreHistoric, status, loadOlder } = useFeed();
  const [filters, setFilters] = useLocalStorage<FilterState>(FILTERS_KEY, DEFAULT_FILTERS);
  const [density, setDensity] = useLocalStorage<Density>(DENSITY_KEY, DEFAULT_DENSITY);

  const listings = useMemo(() => buildFeed(events), [events]);
  const visible = useMemo(() => listings.filter((l) => passesFilter(l, filters)), [listings, filters]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [followBottom, setFollowBottom] = useState(true);

  // tracking signatures for first-render init and prepend detection
  const initializedRef = useRef(false);
  const prevOldestSigRef = useRef<string | null>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const prevEventCountRef = useRef(0);

  // events is sorted ascending; events[0] is the oldest. When older events get
  // prepended, the oldest signature changes and we capture pre-commit scrollHeight
  // so a layout effect can restore the user's anchor after the DOM grows upward.
  if (events.length > 0) {
    const oldestSig = events[0]?.payload.signature ?? null;
    if (
      prevOldestSigRef.current !== null &&
      oldestSig !== null &&
      prevOldestSigRef.current !== oldestSig
    ) {
      const el = containerRef.current;
      if (el && prevScrollHeightRef.current === null) {
        prevScrollHeightRef.current = el.scrollHeight;
      }
    }
    prevOldestSigRef.current = oldestSig;
  }

  // initial scroll-to-bottom once data arrives
  useLayoutEffect(() => {
    if (initializedRef.current) return;
    if (visible.length === 0) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    initializedRef.current = true;
    prevEventCountRef.current = events.length;
  }, [visible.length, events.length]);

  // restore scroll after a prepend (older events loaded)
  useLayoutEffect(() => {
    const cached = prevScrollHeightRef.current;
    if (cached === null) return;
    const el = containerRef.current;
    if (!el) {
      prevScrollHeightRef.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight - cached;
    prevScrollHeightRef.current = null;
  }, [events]);

  // auto-scroll to bottom on new live events when followBottom
  useLayoutEffect(() => {
    const grew = events.length > prevEventCountRef.current;
    prevEventCountRef.current = events.length;
    if (!grew) return;
    if (!followBottom) return;
    if (!initializedRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length, followBottom]);

  // scroll handler: detect bottom-lock + top-load
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setFollowBottom(distFromBottom <= BOTTOM_TOLERANCE);
      if (el.scrollTop <= TOP_LOAD_THRESHOLD && hasMoreHistoric && !loadingOlder) {
        void loadOlder();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMoreHistoric, loadingOlder, loadOlder]);

  const empty = events.length === 0;
  const noMatches = !empty && visible.length === 0;

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setFollowBottom(true);
  };

  return (
    <div className="relative flex flex-col h-screen bg-zinc-950 text-zinc-100">
      <header className="flex items-center justify-between gap-4 px-5 py-3 border-b border-zinc-800 bg-zinc-900/50 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-base font-semibold tracking-tight">Collector Crypt feed</h1>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className={`h-2 w-2 rounded-full ${STATUS_DOT[status]}`} />
            {STATUS_LABELS[status]}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Filters value={filters} onChange={setFilters} />
          <DensityPicker value={density} onChange={setDensity} />
        </div>
      </header>

      <div ref={containerRef} className="flex-1 overflow-y-auto">
        {hasMoreHistoric && !empty && (
          <div className="text-center text-xs text-zinc-500 py-2">
            {loadingOlder ? "loading older…" : "scroll up to load older"}
          </div>
        )}
        {!hasMoreHistoric && !empty && (
          <div className="text-center text-xs text-zinc-600 py-2">
            no older events in retention window
          </div>
        )}
        {empty && (
          <div className="h-full grid place-items-center text-zinc-500 text-sm">
            {status === "connected"
              ? "waiting for first event…"
              : "loading feed…"}
          </div>
        )}
        {noMatches && (
          <div className="grid place-items-center py-10 text-zinc-500 text-sm">
            no listings match current filters
          </div>
        )}
        <ul className="flex flex-col">
          {visible.map((l) => (
            <FeedItem key={l.payload.signature} listing={l} density={density} />
          ))}
        </ul>
      </div>

      {!followBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-4 right-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 text-xs font-medium px-3 py-1.5 rounded-full shadow-lg"
        >
          ↓ jump to live
        </button>
      )}
    </div>
  );
}
